// DLC_09 · Skrzynka odbiorcza e-Doręczeń — wyłącznie dla CEO.
//
// Lista pokazuje nagłówki, które przyszły same. Treść i załączniki pobiera dopiero
// kliknięcie „Odbierz", bo odbiór wiadomości uruchamia bieg terminów — to musi być decyzja
// człowieka, a nie skutek uboczny działania automatu. Po odebraniu pismo idzie do obiegu
// (wpis wpływu w dzienniku DLC_02) albo zostaje zamknięte jako obsłużone poza systemem.
import { useState } from "react";
import { Download, Inbox, Link2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  AttachmentLinks,
  EmptyState,
  HubNav,
  PageHeader,
  TableShell,
  Th,
} from "@/features/power_projects/components/ui";
import { fmtDateTime } from "@/features/power_projects/utils";
import { DLC09_HUB_LINKS, StatusChip } from "../components/ui";
import { useEdoreczeniaSummary, useInbox, useInboxMutations } from "../hooks";
import type { IncomingMessage } from "../types";

function Disposition({ msg }: { msg: IncomingMessage }) {
  const { fetch, file, handleExternally } = useInboxMutations();
  const [mode, setMode] = useState<"" | "file" | "external">("");
  const [note, setNote] = useState("");

  if (msg.status === "new") {
    return (
      <Button size="sm" onClick={() => fetch.mutate(msg.id)} disabled={fetch.isPending}>
        <Download size={14} /> Odbierz
      </Button>
    );
  }

  if (msg.status === "filed") {
    return <span className="text-[11px] text-[var(--color-text-muted)]">W obiegu dokumentów</span>;
  }
  if (msg.status === "external") {
    return (
      <span className="text-[11px] text-[var(--color-text-muted)]">
        Poza systemem{msg.handled_note ? `: ${msg.handled_note}` : ""}
      </span>
    );
  }

  if (mode) {
    return (
      <div className="flex items-center gap-1">
        <input
          className="input-base w-56 text-xs"
          placeholder={mode === "file" ? "Adnotacja do dekretacji" : "Dlaczego poza systemem?"}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          size="sm"
          onClick={() => {
            const done = { onSuccess: () => setMode("") };
            if (mode === "file") file.mutate({ id: msg.id, note }, done);
            else handleExternally.mutate({ id: msg.id, note }, done);
          }}
        >
          Potwierdź
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setMode("")}>
          Anuluj
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Button size="sm" onClick={() => setMode("file")}>
        Przekaż do obiegu
      </Button>
      <Button size="sm" variant="secondary" onClick={() => setMode("external")}>
        Obsłuż poza systemem
      </Button>
    </div>
  );
}

export function InboxPage() {
  const { data: summary } = useEdoreczeniaSummary();
  const { data: messages, isLoading } = useInbox();
  const { sync } = useInboxMutations();

  return (
    <div>
      <PageHeader
        title="Skrzynka odbiorcza e-Doręczeń"
        subtitle="Widoczna wyłącznie dla CEO. Treść pobierana dopiero po kliknięciu „Odbierz”."
        actions={
          <Button variant="secondary" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw size={14} /> Sprawdź teraz
          </Button>
        }
      />
      <HubNav
        links={DLC09_HUB_LINKS}
        badges={{
          "/edoreczenia": summary?.queued ?? 0,
          "/edoreczenia/skrzynka": summary?.inbox_new ?? 0,
        }}
      />

      <div className="mb-4 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2 text-[11px] text-[var(--color-text-secondary)]">
        <Inbox size={12} className="mr-1 inline" />
        Odebranie wiadomości jest czynnością prawną — od tego momentu biegną terminy. Dlatego
        system pobiera wyłącznie nagłówki, a treść ściąga dopiero na Twoje kliknięcie i zapisuje,
        kto i kiedy odebrał.
      </div>

      {isLoading ? (
        <Spinner />
      ) : !messages?.length ? (
        <EmptyState>Brak wiadomości w skrzynce.</EmptyState>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th>Status</Th>
              <Th>Nadawca</Th>
              <Th>Temat</Th>
              <Th>Data</Th>
              <Th>Powiązanie</Th>
              <Th>Załączniki</Th>
              <Th>Odebrał</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {messages.map((msg) => (
              <tr key={msg.id} className="border-t border-[var(--color-border)]">
                <td className="px-2 py-1.5">
                  <StatusChip label={msg.status_display} status={msg.status} kind="incoming" />
                </td>
                <td className="px-2 py-1.5">
                  <div>{msg.sender_name || "—"}</div>
                  <div className="font-mono text-[10px] text-[var(--color-text-muted)]">
                    {msg.sender_ade}
                  </div>
                </td>
                <td className="px-2 py-1.5">{msg.subject || "—"}</td>
                <td className="px-2 py-1.5 text-[11px]">{fmtDateTime(msg.received_at)}</td>
                <td className="px-2 py-1.5 text-[11px]">
                  {msg.linked_outgoing_subject ? (
                    <span className="inline-flex items-center gap-1" title={msg.link_kind_display}>
                      <Link2 size={12} />
                      {msg.linked_outgoing_subject}
                      <span className="text-[var(--color-text-muted)]">({msg.link_kind_display})</span>
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-1.5 text-[11px]">
                  {msg.status === "new" ? (
                    <span className="text-[var(--color-text-muted)]">
                      {msg.attachments_count || 0} (nieodebrane)
                    </span>
                  ) : (
                    <AttachmentLinks files={msg.files} itemClassName="max-w-[180px] truncate" />
                  )}
                </td>
                <td className="px-2 py-1.5 text-[11px]">
                  {msg.fetched_by_name ? (
                    <>
                      {msg.fetched_by_name}
                      <div className="text-[var(--color-text-muted)]">
                        {fmtDateTime(msg.fetched_at)}
                      </div>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Disposition msg={msg} />
                </td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
    </div>
  );
}
